import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Upload, Download, FileCheck, AlertTriangle, CheckCircle2,
  XCircle, Image, FileSpreadsheet, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { BACKEND_URL } from '@/api';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
});

const downloadFile = async (endpoint, filename) => {
  const res = await fetch(`${BACKEND_URL}/api/admin/bulk/${endpoint}`, {
    headers: getAuthHeaders()
  });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const StatusBadge = ({ status }) => {
  if (status === 'valid') return <Badge className="bg-green-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Valid</Badge>;
  if (status === 'warning') return <Badge className="bg-amber-600 text-white"><AlertTriangle className="w-3 h-3 mr-1" />Warning</Badge>;
  return <Badge className="bg-red-600 text-white"><XCircle className="w-3 h-3 mr-1" />Error</Badge>;
};

const ValidationSummary = ({ summary }) => {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-4 gap-3 mb-4" data-testid="validation-summary">
      <div className="bg-gray-700/50 rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-white">{summary.total}</div>
        <div className="text-xs text-gray-400">Total Rows</div>
      </div>
      <div className="bg-green-900/30 border border-green-700/30 rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-green-400">{summary.valid}</div>
        <div className="text-xs text-green-300">Valid</div>
      </div>
      <div className="bg-amber-900/30 border border-amber-700/30 rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-amber-400">{summary.warnings}</div>
        <div className="text-xs text-amber-300">Warnings</div>
      </div>
      <div className="bg-red-900/30 border border-red-700/30 rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-red-400">{summary.errors}</div>
        <div className="text-xs text-red-300">Errors</div>
      </div>
    </div>
  );
};

const ImportSection = ({ type, label, templateEndpoint, templateFilename, exportEndpoints, columns, onRefresh }) => {
  const csvRef = useRef(null);
  const imgRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validation, setValidation] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [showRows, setShowRows] = useState(false);

  const handleValidate = async () => {
    if (!csvFile) return;
    setValidating(true);
    setValidation(null);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('csv_file', csvFile);
      imageFiles.forEach(f => fd.append('images', f));

      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${BACKEND_URL}/api/admin/bulk/validate/${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Validation failed');
      setValidation(data);
    } catch (err) {
      setValidation({ summary: { total: 0, valid: 0, warnings: 0, errors: 1 }, rows: [{ row: 0, errors: [err.message], warnings: [], status: 'error' }] });
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!csvFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('csv_file', csvFile);
      imageFiles.forEach(f => fd.append('images', f));

      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${BACKEND_URL}/api/admin/bulk/import/${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setImportResult(data);
      setValidation(null);
      if (data.created > 0 && onRefresh) onRefresh();
    } catch (err) {
      setImportResult({ errors: [err.message], created: 0 });
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setCsvFile(null);
    setImageFiles([]);
    setValidation(null);
    setImportResult(null);
    if (csvRef.current) csvRef.current.value = '';
    if (imgRef.current) imgRef.current.value = '';
  };

  return (
    <Card className="bg-gray-800/50 border-sky-900/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-white flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-sky-400" />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Action buttons row */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
            onClick={() => downloadFile(templateEndpoint, templateFilename)}
            data-testid={`download-template-${type}`}
          >
            <Download className="w-4 h-4 mr-1.5" /> CSV Template
          </Button>
          {exportEndpoints && exportEndpoints.map(exp => (
            <Button
              key={exp.endpoint}
              variant="outline"
              size="sm"
              className="border-sky-700 text-sky-300 hover:bg-sky-900/30"
              onClick={() => downloadFile(exp.endpoint, exp.filename)}
              data-testid={`export-${exp.endpoint.replace(/\//g, '-')}`}
            >
              <Download className="w-4 h-4 mr-1.5" /> {exp.label}
            </Button>
          ))}
        </div>

        {/* CSV columns info */}
        <p className="text-xs text-gray-500">
          Required columns: <span className="text-gray-300">{columns}</span>
        </p>

        {/* File inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-300 text-sm">CSV File</Label>
            <Input
              type="file"
              accept=".csv"
              ref={csvRef}
              onChange={(e) => { setCsvFile(e.target.files[0]); setValidation(null); setImportResult(null); }}
              className="bg-gray-700 border-gray-600 text-white mt-1"
              data-testid={`csv-input-${type}`}
            />
          </div>
          <div>
            <Label className="text-gray-300 text-sm">Images (select all from folder)</Label>
            <Input
              type="file"
              accept="image/*"
              multiple
              ref={imgRef}
              onChange={(e) => setImageFiles(Array.from(e.target.files))}
              className="bg-gray-700 border-gray-600 text-white mt-1"
              data-testid={`images-input-${type}`}
            />
            {imageFiles.length > 0 && (
              <p className="text-xs text-sky-400 mt-1">
                <Image className="w-3 h-3 inline mr-1" />
                {imageFiles.length} image(s) selected
              </p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            onClick={handleValidate}
            disabled={!csvFile || validating}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid={`validate-btn-${type}`}
          >
            {validating ? <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Checking...</> : <><FileCheck className="w-4 h-4 mr-1.5" /> Validate</>}
          </Button>
          <Button
            onClick={handleImport}
            disabled={!csvFile || importing}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid={`import-btn-${type}`}
          >
            {importing ? <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Importing...</> : <><Upload className="w-4 h-4 mr-1.5" /> Import</>}
          </Button>
          <Button variant="outline" className="border-gray-600 text-gray-400" onClick={reset} data-testid={`reset-btn-${type}`}>
            Reset
          </Button>
        </div>

        {/* Validation Results */}
        {validation && (
          <div className="space-y-3 pt-2" data-testid={`validation-results-${type}`}>
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-amber-400" /> Validation Results
            </h4>
            <ValidationSummary summary={validation.summary} />

            {/* Unmatched images */}
            {validation.summary?.unmatched_images?.length > 0 && (
              <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3">
                <p className="text-sm text-amber-300 font-medium mb-1">Unmatched Images (no CSV row found):</p>
                <div className="flex flex-wrap gap-1">
                  {validation.summary.unmatched_images.map((fn, i) => (
                    <Badge key={i} variant="outline" className="border-amber-600 text-amber-300 text-xs">{fn}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Row details toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
              onClick={() => setShowRows(!showRows)}
            >
              {showRows ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              {showRows ? 'Hide' : 'Show'} Row Details ({validation.rows?.length})
            </Button>

            {showRows && (
              <div className="max-h-64 overflow-y-auto space-y-1">
                {validation.rows?.map((row, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 p-2 rounded text-sm ${
                      row.status === 'error' ? 'bg-red-900/20 border border-red-800/30' :
                      row.status === 'warning' ? 'bg-amber-900/20 border border-amber-800/30' :
                      'bg-green-900/10 border border-green-800/20'
                    }`}
                  >
                    <StatusBadge status={row.status} />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-300 text-xs">Row {row.row}:</span>{' '}
                      <span className="text-white">
                        {row.name || `${row.home_team || ''} vs ${row.away_team || ''}`}
                      </span>
                      {row.matched_image && (
                        <Badge className="ml-2 bg-sky-700/50 text-sky-200 text-xs">
                          <Image className="w-3 h-3 mr-1" />{row.matched_image}
                        </Badge>
                      )}
                      {row.errors?.map((e, j) => <p key={`e${j}`} className="text-red-400 text-xs mt-0.5">{e}</p>)}
                      {row.warnings?.map((w, j) => <p key={`w${j}`} className="text-amber-400 text-xs mt-0.5">{w}</p>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Import Results */}
        {importResult && (
          <div className="bg-gray-900 rounded-lg p-4 space-y-2" data-testid={`import-results-${type}`}>
            <h4 className="text-sm font-semibold text-white">Import Results</h4>
            {importResult.created > 0 && (
              <p className="text-green-400">
                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                Created {importResult.created} {type}
              </p>
            )}
            {importResult.skipped > 0 && (
              <p className="text-amber-400">Skipped {importResult.skipped} (already exist)</p>
            )}
            {importResult.created_names?.length > 0 && (
              <p className="text-sky-300 text-sm">New: {importResult.created_names.join(', ')}</p>
            )}
            {importResult.created_games?.length > 0 && (
              <p className="text-sky-300 text-sm">Games: {importResult.created_games.join(', ')}</p>
            )}
            {importResult.sports_created?.length > 0 && (
              <p className="text-purple-300 text-sm">Sports created: {importResult.sports_created.join(', ')}</p>
            )}
            {importResult.errors?.length > 0 && (
              <div className="mt-2">
                <p className="text-red-400 font-medium text-sm">Errors:</p>
                {importResult.errors.map((e, i) => (
                  <p key={i} className="text-red-300 text-xs">{e}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BulkImportManager = ({ onRefresh }) => {
  return (
    <div className="space-y-6" data-testid="bulk-import-manager">
      <div>
        <h2 className="text-2xl font-bold text-white">Bulk Import Manager</h2>
        <p className="text-gray-400 text-sm mt-1">
          Mass import teams, leagues, and games with auto-matched images. Upload a CSV + image folder, validate entries, then import.
        </p>
      </div>

      <Tabs defaultValue="teams" className="w-full">
        <TabsList className="bg-gray-800/60 mb-4">
          <TabsTrigger value="teams" className="data-[state=active]:bg-sky-500 text-sm" data-testid="tab-teams">Teams</TabsTrigger>
          <TabsTrigger value="leagues" className="data-[state=active]:bg-sky-500 text-sm" data-testid="tab-leagues">Leagues</TabsTrigger>
          <TabsTrigger value="games" className="data-[state=active]:bg-sky-500 text-sm" data-testid="tab-games">Games</TabsTrigger>
        </TabsList>

        <TabsContent value="teams">
          <ImportSection
            type="teams"
            label="Import Teams"
            templateEndpoint="templates/teams"
            templateFilename="teams_template.csv"
            exportEndpoints={[
              { endpoint: 'export/teams', filename: 'teams_export.csv', label: 'Export Teams' },
              { endpoint: 'export/leagues', filename: 'leagues_export.csv', label: 'Export Leagues (reference)' }
            ]}
            columns="name, short_name, league"
            onRefresh={onRefresh}
          />
          <div className="mt-4 bg-gray-800/30 border border-gray-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-white mb-2">Image Matching</h4>
            <p className="text-xs text-gray-400">
              Name your logo files to match the team name. For example, if the CSV has "Celtic FC", 
              name the logo file <span className="text-sky-300">Celtic FC.png</span>. Matching is case-insensitive 
              and flexible — <span className="text-sky-300">celtic fc.png</span> also works.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="leagues">
          <ImportSection
            type="leagues"
            label="Import Leagues"
            templateEndpoint="templates/leagues"
            templateFilename="leagues_template.csv"
            exportEndpoints={[
              { endpoint: 'export/leagues', filename: 'leagues_export.csv', label: 'Export Leagues' }
            ]}
            columns="name, sport"
            onRefresh={onRefresh}
          />
          <div className="mt-4 bg-gray-800/30 border border-gray-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-white mb-2">Image Matching</h4>
            <p className="text-xs text-gray-400">
              Name your logo files to match the league name. E.g., <span className="text-sky-300">APSL.png</span> matches the "APSL" league row.
              Sports not found will be auto-created.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="games">
          <ImportSection
            type="games"
            label="Import Games"
            templateEndpoint="templates/games"
            templateFilename="games_template.csv"
            exportEndpoints={[
              { endpoint: 'export/leagues', filename: 'leagues_export.csv', label: 'Export Leagues (reference)' },
              { endpoint: 'export/teams', filename: 'teams_export.csv', label: 'Export Teams (reference)' }
            ]}
            columns="home_team, away_team, league, scheduled_time, venue, stream_url, stream_type, is_featured"
            onRefresh={onRefresh}
          />
          <div className="mt-4 bg-gray-800/30 border border-gray-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-white mb-2">Important Notes</h4>
            <ul className="text-xs text-gray-400 space-y-1 list-disc pl-4">
              <li>Teams and leagues <span className="text-red-300">must exist</span> before importing games. Import them first!</li>
              <li>Use the "Export Leagues" and "Export Teams" buttons to get exact names for your CSV.</li>
              <li>Image matching: name thumbnails as <span className="text-sky-300">HomeTeam vs AwayTeam.jpg</span> or just the home team name.</li>
              <li>Date format: <span className="text-sky-300">YYYY-MM-DDTHH:MM</span> (e.g., 2026-07-15T19:00)</li>
            </ul>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BulkImportManager;
